import { t } from 'i18next';
import {
  get,
  isArray,
  isDate,
  isNil,
  isNumber,
  omit,
  omitBy,
  pick,
  uniqBy,
} from 'lodash';
import moment from 'moment-timezone';
import * as Yup from 'yup';
import {
  AccountInfoType,
  ColumnSettingsType,
  ConditionType,
  ContactListType,
  FieldChoicesType,
  FormServFieldType,
  McrContactType,
  RuleType,
} from '../shared/types';
import {
  COLUMNS_HIDE_ORDERING_KEY,
  CONTACT_CUSTOM_FIELD_NUMBER_LIMIT,
  FREEMIUM_CHECKBOX_TYPES,
  FREEMIUM_DROPDOWN_TYPES,
  FREEMIUM_INPUT_DATE_TIME_TYPES,
  FREEMIUM_INPUT_DATE_TYPES,
  FREEMIUM_INPUT_NUMBER_TYPES,
  FREEMIUM_MULTISELECT_DROPDOWN_TYPES,
  FREEMIUM_RADIO_TYPES,
  GLOBAL_DATE_FORMAT,
  LOCAL_INPUT_DATE_TIME_FORMAT,
  YUP_ARRAY_FIELD_TYPES,
  YUP_BOOLEAN_FIELD_TYPES,
  YUP_DATE_FIELD_TYPES,
  YUP_DATE_TIME_FIELD_TYPES,
  YUP_MIXED_FIELD_TYPES,
  YUP_NUMBER_FIELD_TYPES,
  YUP_OBJECT_FIELD_TYPES,
  YUP_STRING_FIELD_TYPES,
} from '../shared/constants';
import { getAccountTimeFormat, isValidDateString } from '../shared/utils';
import { DefaultFilterViews } from './screens/contacts/constants';
import { MCR_DEFAULT_FIELDS } from './constants';
import i18n from '../i18n';

type ColumnOptions = any;

export const blockInvalidInteger = (e: React.KeyboardEvent) =>
  ['e', 'E', '+', '-', '.'].includes(e.key) && e.preventDefault();

export const blockInvalidDecimal = (e: React.KeyboardEvent) =>
  ['e', 'E', '+', '-'].includes(e.key) && e.preventDefault();

export const getSchemaFromFormServFields = (
  columns: FormServFieldType[],
): Record<string, Yup.AnySchema> => {
  const fieldValidation: any = {};
  columns.forEach((column: FormServFieldType) => {
    let validation;
    // Skip regex for phone_number field
    if (column.regex?.validation && column.type !== 4) {
      validation = Yup.string()
        .trim()
        .matches(new RegExp(column.regex.validation), {
          message: column.regex.message || '',
          excludeEmptyString: true,
        });
    } else if (YUP_STRING_FIELD_TYPES.includes(column.type)) {
      validation = Yup.string().trim();
      if (column.type === 3) {
        validation = validation.email(`${t('forms.err_msg.email_is_invalid')}`);
      }
      if (column.type === 10 || column.name == 'company_website') {
        validation = validation.url(`${t('forms.err_msg.url_is_invalid')}`);
      }
    } else if (YUP_NUMBER_FIELD_TYPES.includes(column.type)) {
      validation = Yup.number()
        .transform((num) =>
          isNaN(num) || isNil(num) ? null : Number(`${num}`),
        )
        .integer(`${t('forms.err_msg.integer')}`)
        .nullable()
        .test({
          test: (value: any) => {
            if (value) return Number(value) < CONTACT_CUSTOM_FIELD_NUMBER_LIMIT;
            return true;
          },
          message: `${t('forms.err_msg.integer_is_invalid')}`,
        });
    } else if (YUP_BOOLEAN_FIELD_TYPES.includes(column.type)) {
      validation = Yup.boolean();
    } else if (
      [...YUP_DATE_TIME_FIELD_TYPES, ...YUP_DATE_FIELD_TYPES].includes(
        column.type,
      )
    ) {
      validation = Yup.string().test({
        test: (value: any) => {
          if (column.required || !!value)
            return value === undefined || isValidDateString(value);
          return true;
        },
        message: `${t('forms.err_msg.valid_date')}`,
      });
    } else if (YUP_OBJECT_FIELD_TYPES.includes(column.type)) {
      validation = Yup.object();
    } else if (YUP_MIXED_FIELD_TYPES.includes(column.type)) {
      validation = Yup.mixed();
    } else if (YUP_ARRAY_FIELD_TYPES.includes(column.type)) {
      validation = Yup.array();
    }
    if (column.required) {
      validation = validation
        .required(
          `${t('forms.err_msg.label_required', {
            label: `${column.label}`,
          })}`,
        )
        .typeError(
          `${t('forms.err_msg.label_required', {
            label: `${column.label}`,
          })}`,
        );
    } else {
      validation = validation.nullable();
    }
    fieldValidation[column.name] = validation;
  });
  return fieldValidation;
};

export const getInputValue = (
  type?: number,
  value?: any,
  accountInfo?: AccountInfoType,
) => {
  switch (type) {
    case 1:
      return value ? String(value) : undefined;
    case 5:
      return typeof value === 'boolean' ? value : value === 'true';
    case 7:
      return value && isValidDateString(value)
        ? new Date(
            moment(value)
              .tz(accountInfo?.timezone || moment.tz.guess())
              .format(LOCAL_INPUT_DATE_TIME_FORMAT),
          )
        : undefined;
    case 17:
      return value && isValidDateString(value)
        ? moment(value).startOf('day').toDate()
        : undefined;
    case 8:
      return value && isNumber(Number(`${value}`)) ? Number(`${value}`) : value;
    default:
      return value;
  }
};

export const setInputValue = (
  type?: number,
  value?: any,
  accountInfo?: AccountInfoType,
) => {
  if (!value) return value;
  switch (type) {
    case 1:
      return String(value);
    // date_time
    case 7:
      return value && isDate(value)
        ? moment(value)
            .tz(accountInfo?.timezone || moment.tz.guess(), true)
            .toISOString()
        : value;
    // date
    case 17:
      return value && isDate(value)
        ? moment(value)
            .tz(accountInfo?.timezone || moment.tz.guess(), true)
            .format(GLOBAL_DATE_FORMAT)
        : value;
    case 8:
      return Number(value);
    default:
      return value;
  }
};

const isEmpty = (value) => {
  return (
    value === undefined ||
    value === null ||
    (typeof value === 'object' && Object.keys(value).length === 0) ||
    (typeof value === 'string' && value.trim().length === 0)
  );
};

export const getDisplayValueFromField = ({
  field,
  rowValues,
  contactLists,
  emptyPlaceholder = '',
  accountInfo,
}: {
  field: FormServFieldType | { [key: string]: any };
  accountInfo?: AccountInfoType;
  rowValues: McrContactType | { [key: string]: any };
  contactLists: ContactListType[];
  emptyPlaceholder?: string;
}) => {
  const choices: FieldChoicesType[] = get(field, ['choices'], []) || [];
  const allChoices: { [key: string]: FieldChoicesType } = {};
  choices.forEach((choice) => {
    allChoices[choice.id || ''] = choice;
  });
  const value = field.custom
    ? get(rowValues, ['fields', field.name], '')
    : get(rowValues, [field.name], '');
  if (isNil(value) || isEmpty(value)) return emptyPlaceholder;
  if (
    [...FREEMIUM_RADIO_TYPES, ...FREEMIUM_DROPDOWN_TYPES].includes(field.type)
  ) {
    if (choices.length === 0) return value;
    const choice = allChoices[value];
    const fieldId = get(choice, ['id'], value);
    return i18n.exists(
      `contacts.import_contact.form_choices.${field.name}.${fieldId}`,
    )
      ? `${t(`contacts.import_contact.form_choices.${field.name}.${fieldId}`)}`
      : get(choice, ['value'], value);
  }
  if (FREEMIUM_MULTISELECT_DROPDOWN_TYPES.includes(field.type)) {
    if (field.name === 'list_ids') {
      if (contactLists.length === 0) return value;
      const allContactLists: { [key: string]: ContactListType } = {};
      (contactLists || []).forEach((contactList: ContactListType) => {
        allContactLists[contactList.display_id || ''] = contactList;
      });
      const returnValue =
        isArray(value) && isArray(contactLists)
          ? value
              .map((id) => get(allContactLists, [id, 'label'], ''))
              .join(', ')
          : value;
      return returnValue;
    }
    if (choices.length === 0) return value;
    return isArray(value) && isArray(choices)
      ? value.map((id) => allChoices[id].value).join(', ')
      : value;
  }
  if (FREEMIUM_INPUT_DATE_TIME_TYPES.includes(field.type)) {
    const date = new Date(value);
    const hasTimeData = [
      date.getUTCHours(),
      date.getUTCMinutes(),
      date.getUTCSeconds(),
      date.getUTCMilliseconds(),
    ].some((time) => time !== 0);

    return (
      moment(date)
        .tz(accountInfo?.timezone || moment.tz.guess())
        .format(
          hasTimeData ? getAccountTimeFormat(accountInfo) : GLOBAL_DATE_FORMAT,
        ) || value
    );
  }
  if (FREEMIUM_INPUT_DATE_TYPES.includes(field.type)) {
    return (
      moment(new Date(value))
        .tz(accountInfo?.timezone || moment.tz.guess())
        .format(GLOBAL_DATE_FORMAT) || value
    );
  }
  if (FREEMIUM_INPUT_NUMBER_TYPES.includes(field.type)) {
    return !Number.isNaN(Number(value)) ? Number(value) : value;
  }
  if (FREEMIUM_CHECKBOX_TYPES.includes(field.type)) {
    return value ? 'Yes' : 'No';
  }
  return value;
};

export const getTableColumnsFromFields = ({
  fields = [],
  contactLists = [],
  skipFields = [],
  columnSettings,
  accountInfo,
}: {
  skipFields?: string[];
  fields: FormServFieldType[];
  contactLists: ContactListType[];
  columnSettings: ColumnSettingsType;
  accountInfo?: AccountInfoType;
}) => {
  const columns: ColumnOptions[] = [];
  fields.forEach((field: FormServFieldType) => {
    const column: any = {
      ...field,
      ...(columnSettings[field.name] || {}),
    };
    const col: any = omitBy(
      {
        id: column[COLUMNS_HIDE_ORDERING_KEY],
        accessor: skipFields.includes(column[COLUMNS_HIDE_ORDERING_KEY])
          ? undefined
          : column.accessor
          ? column.accessor
          : (row: any) =>
              getDisplayValueFromField({
                field: column,
                rowValues: row,
                contactLists,
                accountInfo,
              }),
        Header: field.field_options?.fs_default
          ? `${t(
              `contacts.import_contact.default_fields.contact.${field?.name}`,
            )}`
          : column.label,
        minWidth: column.minWidth,
        width: column.width,
      },
      isNil,
    );
    columns.push(col);
  });
  return columns;
};

export const getContactPayload: (formValues: any) => McrContactType = (
  formValues = [],
) => {
  const defaultFields = pick(formValues, MCR_DEFAULT_FIELDS);
  // deleted is a field in our side
  const customFields = omit(formValues, [...MCR_DEFAULT_FIELDS, 'deleted']);
  Object.keys(defaultFields).forEach((name) => {
    if (!formValues[name]) {
      // MCR expects null values inside fields if they need to be cleared
      customFields[name] = null;
      defaultFields[name] = null;
    }
  });
  return {
    ...defaultFields,
    fields: customFields,
  };
};

export const getSegmentPayloadFromFilters: (
  filterData: any,
  defaultCondition?: ConditionType,
  filterViewId?: string,
) => RuleType = (filterData = {}, defaultCondition, filterViewId) => {
  const filterFields: any[] = [];
  const fieldNames = Object.keys(filterData);
  const filterViewConditions =
    DefaultFilterViews.find((defaultFilterView) => {
      return defaultFilterView.id === filterViewId;
    })?.conditions || DefaultFilterViews[0].conditions;
  const SEGMENT_PAYLOAD: RuleType = {
    match_type: 'ALL',
    condition_sets: [
      {
        match_type: 'ALL',
        conditions:
          defaultCondition &&
          defaultCondition.operator &&
          defaultCondition.name &&
          defaultCondition.field_type
            ? [defaultCondition, ...filterViewConditions]
            : [...filterViewConditions],
      },
    ],
  };
  if (fieldNames.length === 0) return SEGMENT_PAYLOAD;
  fieldNames.forEach((fieldName: string) => {
    if (filterData[fieldName]) filterFields.push(filterData[fieldName]);
  });

  return {
    match_type: 'ALL',
    condition_sets: [
      {
        match_type: 'ALL',
        conditions: uniqBy(
          [defaultCondition, ...filterViewConditions, ...filterFields].filter(
            (condition) => !isNil(condition),
          ),
          'name',
        ),
      },
    ],
  };
};

export const isTagValid = (tagName: string) => {
  const pattern = /^([a-zA-Z0-9]+\s)*[-_a-zA-Z0-9]+$/i;
  return pattern.test(tagName);
};

export const capitalize = (name: string) => {
  return name.charAt(0).toUpperCase() + name.slice(1);
};
